import type {
  GroupSummaryValueExtractor,
  BmGridColumnSortState,
  BmGridConfiguration,
} from "@/components/controls/grid/BmGridData";
import {
  BmGridState,
  componentToRenderer,
} from "@/components/controls/grid/BmGridData";
import type { Instant, LocalTime } from "@js-joda/core";
import { LocalDate, DateTimeFormatter } from "@js-joda/core";
import type { ColumnKey } from "@/components/controls/grid/builder/ColumnBuilders";
import {
  ActionColumnBuilder,
  ColumnBuilder,
  ControlColumnBuilder,
  NumberColumnBuilder,
} from "@/components/controls/grid/builder/ColumnBuilders";
import {
  IconSummaryBuilder,
  NumberSummaryBuilder,
  SummaryBuilder,
} from "@/components/controls/grid/builder/SummaryBuilders";
import type { BmEnumType } from "@/app/services/baseEnumType";
import type BaseEnumType from "@/app/services/baseEnumType";
import DataCellIcon from "@/components/controls/grid/renderers/DataCellIcon.vue";
import type { VueConstructor } from "vue";
import DataCellControl from "@/components/controls/grid/renderers/DataCellControl.vue";
import TextFilterComponent from "@/components/controls/grid/columnfilters/TextFilterComponent.vue";
import { buildApplyTextFilter } from "@/components/controls/grid/columnfilters/TextColumnFilterApply";
import NumberFilterComponent from "@/components/controls/grid/columnfilters/NumberFilterComponent.vue";
import { buildApplyNumberFilter } from "@/components/controls/grid/columnfilters/NumberColumnFilterApply";
import DateFilterComponent from "@/components/controls/grid/columnfilters/DateFilterComponent.vue";
import { buildApplyDateFilter } from "@/components/controls/grid/columnfilters/DateColumnFilterApply";
import DateTimeFilterComponent from "@/components/controls/grid/columnfilters/DateTimeFilterComponent.vue";
import { buildApplyDateTimeFilter } from "@/components/controls/grid/columnfilters/DateTimeColumnFilterApply";
import type {
  VolumeUnitPreference,
  WeightUnitPreference,
} from "@/app/services/extensions/tenantServiceExtended";
import {
  resolveVolumeUnitPreference,
  resolveWeightUnitPreference,
  tenantService,
} from "@/app/services/extensions/tenantServiceExtended";
import type { UnitOfMeasure } from "@/app/services/generated/unitofmeasureService";
import EnumFilterComponent from "@/components/controls/grid/columnfilters/EnumFilterComponent.vue";
import { buildApplyEnumFilter } from "@/components/controls/grid/columnfilters/EnumColumnFilterApply";
import BooleanFilterComponent from "@/components/controls/grid/columnfilters/BooleanFilterComponent.vue";
import { buildApplyBooleanFilter } from "@/components/controls/grid/columnfilters/BooleanColumnFilterApply";
import { screenSize } from "@/vue23switching/screenSize";
import Key from "@/app/types/common/key";
import DataCellBoolean from "@/components/controls/grid/renderers/DataCellBoolean.vue";
import type { TruncateOptions } from "lodash";
import { dateMatcher } from "@/components/controls/grid/BmGridMatchers";
import type { ComponentPublicInstanceConstructor } from "vue/types/v3-component-public-instance";
import { keys, translate } from "@/plugins/i18n";
import type { Maybe, StringableId } from "@/components/utils/typeHelpers";
import { EnumCurrency } from "@/app/services/generated/enums/enumCurrency.generated";
import { formatCurrency } from "@/app/types/common/currency";
import type { BmIconName } from "@/components/controls/bmIconMap";

export default class GridConfigurationBuilder<T> {
  private readonly _columnBuilders: ColumnBuilder<any, any>[] = [];
  private readonly _summaryBuilders: SummaryBuilder<any, any>[] = [];
  private readonly _itemKey?: (item: T) => string;
  private readonly _title?: string;
  private readonly _onRowClick?: (item: T) => void;

  // Will mostly get Guid Ids but can deal with anything resolving to a string.
  constructor(opts?: {
    itemId?: (item: T) => StringableId;
    title?: string;
    onRowClick?: (item: T) => void;
  }) {
    this._itemKey = opts?.itemId
      ? (item) => opts.itemId!(item).toString()
      : undefined;
    this._title = opts?.title;
    this._onRowClick = opts?.onRowClick;
  }

  addTextColumn(
    columnKey: ColumnKey,
    itemValue: (item: T) => Maybe<string>
  ): ColumnBuilder<T, string> {
    const builder = new ColumnBuilder<T, string>(columnKey, itemValue);
    builder.withColumnFilter({
      columnFilterComponent: TextFilterComponent,
      buildApply: buildApplyTextFilter,
    });

    this._columnBuilders.push(builder);
    return builder;
  }

  addEnumColumn2<TEnum extends BaseEnumType>(
    columnKey: ColumnKey,
    itemValue: (item: T) => TEnum | null,
    options: readonly TEnum[] | (() => readonly TEnum[])
  ): ColumnBuilder<T, string> {
    const resolve = () => (typeof options === "function" ? options() : options);

    const builder = new ColumnBuilder<T, BaseEnumType>(columnKey, (item) => {
      const enumValue = itemValue(item);
      if (!enumValue) {
        return "";
      }
      return enumValue.translationKey
        ? translate(Key.fromString(enumValue.translationKey)).toString()
        : enumValue.value;
    }).withAlignment("start");
    builder.withColumnFilter({
      columnFilterComponent: EnumFilterComponent,
      buildApply: buildApplyEnumFilter,
    });

    builder.withEnumType(() => resolve());

    this._columnBuilders.push(builder);
    return builder;
  }

  addEnumColumn(
    columnKey: ColumnKey,
    itemValue: (item: T) => BaseEnumType | null,
    showZeros: boolean = false,
    enumType: BmEnumType
  ): ColumnBuilder<T, string> {
    return this.addEnumColumn2(
      columnKey,
      (item) => {
        const enumValue = itemValue(item);
        if (!showZeros && enumValue?.key === 0) {
          return null;
        }
        return enumValue;
      },
      () =>
        (enumType.getValues() as readonly BaseEnumType[]).filter(
          (value) => value.key !== 0
        )
    );
  }

  addMobileOnlyTextInfoDialogColumn(
    translationKey: Key,
    itemValue: (item: T) => string | null,
    truncateOptions: TruncateOptions = { length: 100 },
    allowSortingAndFiltering = false
  ): ColumnBuilder<T, string> {
    const column = this.addTextColumn(translationKey, (item) =>
      itemValue(item)
    ).allowSortingAndFiltering(allowSortingAndFiltering);

    if (screenSize.xs) {
      column.withTextTruncation({ length: truncateOptions.length });
    }

    this._columnBuilders.push(column);
    return column;
  }

  addIconColumn(
    columnKey: ColumnKey,
    itemValue: (item: T) => any,
    itemToIconName: (item: any) => Maybe<BmIconName>
  ): ColumnBuilder<T, any> {
    const builder = new ColumnBuilder<T, any>(columnKey, itemValue)
      .withFormatter(itemToIconName)
      .withRenderer(componentToRenderer(DataCellIcon));
    builder.withColumnFilter({
      columnFilterComponent: BooleanFilterComponent,
      buildApply: buildApplyBooleanFilter,
    });
    builder.withAlignment(screenSize.xs ? "start" : "center");
    this._columnBuilders.push(builder);
    return builder;
  }

  addBooleanCheckMarkColumn(
    columnKey: ColumnKey,
    itemValue: (item: T) => boolean | null
  ): ColumnBuilder<T, boolean> {
    const builder = new ColumnBuilder<T, any>(columnKey, itemValue)
      .withRenderer(componentToRenderer(DataCellBoolean))
      .withColumnFilter({
        columnFilterComponent: BooleanFilterComponent,
        buildApply: buildApplyBooleanFilter,
      })
      .withAlignment(screenSize.xs ? "start" : "center")
      .withCsvFormatter((value: boolean, item, stringRenderer) =>
        value ? stringRenderer("True") : stringRenderer("False")
      );
    this._columnBuilders.push(builder);
    return builder;
  }

  addDateColumn(
    columnKey: ColumnKey,
    itemValue: (item: T) => Maybe<LocalDate>
  ): ColumnBuilder<T, Maybe<LocalDate>> {
    // Some non-nullable Date fields contain an erroneous 01/01/1970
    // Which we will treat as null.
    const itemValWithEpochAsNull = (item: T) => {
      const basic = itemValue(item);
      return basic?.equals(LocalDate.EPOCH_0) ? null : basic;
    };

    const builder = new ColumnBuilder<T, LocalDate | null>(
      columnKey,
      itemValWithEpochAsNull
    )
      .withFormatter(
        (localDate) => localDate && tenantService.formatDate(localDate)
      )
      .withCsvFormatter((value: LocalDate | null) => {
        return value
          ? DateTimeFormatter.ofPattern("yyyy-MM-dd").format(value)
          : "";
      })
      .withMatcher(dateMatcher);
    builder.withColumnFilter({
      columnFilterComponent: DateFilterComponent,
      buildApply: buildApplyDateFilter,
    });
    this._columnBuilders.push(builder);
    return builder;
  }

  addTimeColumn(
    columnKey: Key,
    itemValue: (item: T) => LocalTime | null
  ): ColumnBuilder<T, LocalTime | null> {
    const builder = new ColumnBuilder<T, LocalTime | null>(columnKey, itemValue)
      .withFormatter(
        (localTime: LocalTime | null) =>
          localTime && tenantService.formatTime(localTime)
      )
      .withCsvFormatter((value: LocalTime) => {
        return value
          ? DateTimeFormatter.ofPattern("HH:mm:ss").format(value)
          : "";
      });
    this._columnBuilders.push(builder);
    return builder;
  }

  addDateTimeColumn(
    columnKey: ColumnKey,
    itemValue: (item: T) => Maybe<Instant>
  ): ColumnBuilder<T, Maybe<Instant>> {
    const builder = new ColumnBuilder<T, Maybe<Instant>>(columnKey, itemValue)
      .withFormatter(
        (dateTime) => dateTime && tenantService.formatDateTime(dateTime)
      )
      .withCsvFormatter((value: Instant) => {
        return value
          ? DateTimeFormatter.ofPattern("yyyy-MM-dd HH:mm:ss").format(
              tenantService.instantToDateTime(value)
            )
          : "";
      });
    builder.withColumnFilter({
      columnFilterComponent: DateTimeFilterComponent,
      buildApply: buildApplyDateTimeFilter,
    });
    this._columnBuilders.push(builder);
    return builder;
  }

  addNumberColumn(
    columnKey: ColumnKey,
    itemValue: (item: T) => Maybe<number>,
    opts?: {
      integral?: boolean;
      minDecimalPlaces?: number;
      maxDecimalPlaces?: number;
    }
  ): NumberColumnBuilder<T> {
    const builder = new NumberColumnBuilder<T>(columnKey, itemValue, opts);
    builder.withAlignment(screenSize.xs ? "start" : "end");
    this._columnBuilders.push(builder);
    return builder;
  }

  addIntegerColumn(
    columnKey: ColumnKey,
    itemValue: (item: T) => Maybe<number>
  ): NumberColumnBuilder<T> {
    return this.addNumberColumn(columnKey, itemValue, { integral: true });
  }

  addCurrencyColumn(
    columnKey: ColumnKey,
    itemValue: (item: T) => Maybe<number>,
    quantise: boolean = false,
    overrideCurrencyCode: (item: T) => EnumCurrency | undefined | null = () =>
      EnumCurrency.none
  ): ColumnBuilder<T, number> {
    const builder = new ColumnBuilder<T, number>(columnKey, itemValue);

    builder.withFormatter((value, item) => {
      const overrideCurrency = item
        ? (overrideCurrencyCode(item) ?? EnumCurrency.none)
        : EnumCurrency.none;

      // null/undefined is empty whereas 0 is explicitly 0.
      return value != null
        ? overrideCurrency.isNone()
          ? tenantService.formatCurrency(value, quantise)
          : formatCurrency(overrideCurrency, value, quantise)
        : "";
    });
    builder.withColumnFilter({
      columnFilterComponent: NumberFilterComponent,
      buildApply: buildApplyNumberFilter,
    });

    builder.withAlignment(screenSize.xs ? "start" : "end");

    this._columnBuilders.push(builder);
    return builder;
  }

  addMeasureColumn(
    columnKey: ColumnKey,
    itemValue: (item: T) => number | null,
    unitOfMeasureExtractor: (item: T) => Readonly<UnitOfMeasure> | null
  ): NumberColumnBuilder<T> {
    const builder = new NumberColumnBuilder<T>(columnKey, itemValue, {
      integral: false,
    });
    builder.withColumnFilter({
      columnFilterComponent: NumberFilterComponent,
      buildApply: buildApplyNumberFilter,
    });

    builder.withSuffixExtractor((item) => {
      return unitOfMeasureExtractor(item)?.name || "";
    });

    this._columnBuilders.push(builder);
    return builder;
  }

  addWeightColumn(
    columnKey: ColumnKey,
    itemValue: (item: T) => number | null,
    maxDecimalPlaces?: number,
    context: WeightUnitPreference = tenantService.settings
      .displayUnitPreferences.generalWeights
  ): NumberColumnBuilder<T> {
    const unitPreferences = resolveWeightUnitPreference(context);

    const builder = new NumberColumnBuilder<T>(
      columnKey,
      (val: T) => {
        const value = itemValue(val);
        return value != null
          ? tenantService.toDisplayPreferenceWeight(value, maxDecimalPlaces)
          : null;
      },
      {
        integral: false,
        maxDecimalPlaces: maxDecimalPlaces ?? unitPreferences.maxDecimalPlaces,
      }
    );
    builder.withColumnFilter({
      columnFilterComponent: NumberFilterComponent,
      buildApply: buildApplyNumberFilter,
    });

    builder.withSuffixExtractor((item) => unitPreferences.suffix);

    this._columnBuilders.push(builder);
    return builder;
  }

  addVolumeColumn(
    columnKey: ColumnKey,
    itemValue: (item: T) => number | null,
    context: VolumeUnitPreference,
    maxDecimalPlaces?: number
  ): NumberColumnBuilder<T> {
    const unitPreferences = resolveVolumeUnitPreference(context);

    const builder = new NumberColumnBuilder<T>(
      columnKey,
      (val: T) => {
        const value = itemValue(val);
        return value != null
          ? tenantService.toDisplayPreferenceVolume(
              value,
              context,
              maxDecimalPlaces
            )
          : null;
      },
      {
        integral: false,
        maxDecimalPlaces: maxDecimalPlaces ?? unitPreferences.maxDecimalPlaces,
      }
    );

    builder.withColumnFilter({
      columnFilterComponent: NumberFilterComponent,
      buildApply: buildApplyNumberFilter,
    });

    builder.withSuffixExtractor((item) => unitPreferences.suffix);

    this._columnBuilders.push(builder);
    return builder;
  }

  addActionsColumn(columnKey: Key = keys.grid_actions): ActionColumnBuilder<T> {
    const builder = new ActionColumnBuilder<T>(columnKey).showNarrow();
    this._columnBuilders.push(builder);
    return builder;
  }

  addControlColumn(
    columnKey: ColumnKey,
    component: VueConstructor | ComponentPublicInstanceConstructor,
    propBinder?: (item: T) => any,
    eventBinder?: (item: T) => any,
    contentBinder?: (item: T) => any,
    itemValue?: (item: T) => any
  ): ControlColumnBuilder<T> {
    const builder = new ControlColumnBuilder<T>(columnKey, itemValue);
    builder.withRenderer(componentToRenderer(DataCellControl));
    // Don't output anything to csv by default: Caller has to specify this
    builder.withCsvFormatter(() => "");
    builder.withControl(component, propBinder, eventBinder, contentBinder);
    this._columnBuilders.push(builder);
    return builder;
  }

  // Summaries
  addColumnAlignedTextSummary(
    columnKey: Key,
    aggregator: GroupSummaryValueExtractor
  ): SummaryBuilder<T, string> {
    const summary = new SummaryBuilder<T, string>(
      (item, col) => col[columnKey.value].extractor(item),
      aggregator
    ).alignWithGroupedColumn(columnKey.value);
    this._summaryBuilders.push(summary);
    return summary;
  }

  addColumnAlignedNumberSummary(
    columnKey: Key,
    aggregator: GroupSummaryValueExtractor
  ): NumberSummaryBuilder<T> {
    const summary = new NumberSummaryBuilder<T>(
      (item, col) => col[columnKey.value].extractor(item),
      aggregator
    ).alignWithGroupedColumn(columnKey.value);
    this._summaryBuilders.push(summary);
    return summary;
  }

  addColumnAlignedIconSummary(
    columnKey: Key,
    aggregator: GroupSummaryValueExtractor
  ): IconSummaryBuilder<T> {
    const summary = new IconSummaryBuilder<T>(
      (item, col) => col[columnKey.value].extractor(item),
      aggregator
    )
      .alignWithGroupedColumn(columnKey.value)
      .withRenderer(componentToRenderer(DataCellIcon));
    this._summaryBuilders.push(summary);
    return summary;
  }

  build(): BmGridConfiguration {
    const columnStates = this._columnBuilders.map((cb) => cb.getDefaultState());

    const defaultState: Partial<BmGridState> = {
      sortBy: columnStates
        .filter((kv) => kv[1].sortByIndex !== undefined)
        .sort((v1, v2) => v1[1].sortByIndex! - v2[1].sortByIndex!)
        .map<BmGridColumnSortState>((kv) => ({
          columnKey: kv[0],
          descending: kv[1].sortByDescending ?? false,
        })),

      groupBy: columnStates
        .filter((kv) => kv[1].groupByIndex !== undefined)
        .sort((v1, v2) => v1[1].groupByIndex! - v2[1].groupByIndex!)
        .map((kv) => kv[0]),

      removedColumnKeys: columnStates
        .filter((kv) => kv[1].hidden)
        .map((kv) => kv[0]),

      columnFilters: Object.fromEntries(
        columnStates
          .filter((kv) => kv[1].filterState)
          .map((kv) => [kv[0], kv[1].filterState])
      ),

      columnOrder: columnStates.map((kv) => kv[0]),
    };

    return {
      defaultGridState: new BmGridState(defaultState),
      columns: Object.fromEntries(this._columnBuilders.map((cb) => cb.build())),
      summaries: this._summaryBuilders.map((sb) => sb.build()),
      itemKey: this._itemKey,
      title: this._title,
      onRowClick: this._onRowClick,
    };
  }
}
